import {
  FilterOptionSliceType,
  OrderByTypeEnum,
  PriceRangeFilterOptionType,
} from '../../../../interfaces/src/lib/types/index';
import { FilterTypes, QueryTypes } from '../../../../interfaces/src/lib/enums/index';
import { store } from '../stateManager/store';
import { isDealsPage } from '../utilFuncs/dealsManagement';
import {
  formatCruiseId,
  formatGuestsFilter,
  formatPortOfCalls,
  formatPriceCode,
  formatPriceType,
  formatSelectedNumberOfNights,
} from '../genericUtils/genericUtils';

import { getDealsFilters } from '../utilFuncs/contextAwareUtils';
import { normalizeAirportFilterValue } from '../utilFuncs/filterValueUtils';
import { mscGet } from '../utilFuncs/mscStorageClient';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export function appendIsPackage(query: string) {
  const trimmed = query.trim();
  return trimmed.includes('isPackage:false')
    ? trimmed
    : trimmed
    ? `${trimmed} AND isPackage:false`
    : 'isPackage:false';
}

// Utility per rimuovere isPackage:false
export function removeIsPackage(query: string) {
  return query
    .replace(/\sAND\sisPackage:false/g, '')
    .replace(/isPackage:false/g, '')
    .trim();
}

export function buildSelectedFilterQueryString(
  isDatesLayer?: boolean,
  toUpdateButton = false,
  isMultifilter = false,
  isItineraryRequest = false,
  isWishlist = false,
  ignorePriceFiltersForDatesModal?: boolean
): string {
  const state = store.getState();
  const dealPage = isDealsPage();
  const dealPageFilters = getDealsFilters();
  const selectedDestinations = state.facetFilter.selectedDestinations;
  const selectedShips = state.facetFilter.selectedShips;
  const selectedPorts = state.facetFilter.selectedPorts;
  const selectedCabins = state.facetFilter.selectedCabins;
  const selectedAirports = state.facetFilter.selectedAirports;
  const airportToggleEnabled = state.facetFilter.isAirportToggleEnabled;
  const selectedNumberOfNights = state.genericFilter.numberOfNights;
  const selectedPromotions = state.genericFilter.promotion;
  const selectedTags = state.genericFilter.tag;
  const selectedFlyAndCruise = state.genericFilter.flyAndCruise;
  const selectedGuests = state.guestFilter;
  const selectedDates = state.dateFilter.selectedDates;
  const selectedPeriods = state.dateFilter.selectedPeriods;
  const selectedPrices = state.facetFilter.finalPriceRange;
  const cruiseId = state.facetFilter.selectedCruiseId;
  const portOfCall = state.facetFilter.selectedPortOfCall;
  const priceTypeFiltered = state.facetFilter.selectedPriceType;
  const priceCode = state.facetFilter.selectedPriceCode;
  const lastFilterUpdated = state.lastFilterUpdated.filterType;
  const activeFilterIds = state.activeFilter.activeFilterIds;
  let selectedItineraryCodes: string[] = [];

  if (isWishlist) selectedItineraryCodes = mscGet<string[]>('local', 'wishlist') ?? [];

  // build the filters based on the selected destinations
  let destinationFilters: string = selectedDestinations
    .map((dest) => {
      const clauses = dest.value
        .split('-')
        .filter((d) => d && checkFilterValueInFacetsData('commArea.key', null, d))
        .map((d) => `commArea.key:"${d}"`)
        .filter(Boolean)
        .join(' OR ');

      return clauses ? `(${clauses})` : '';
    })
    .filter(Boolean)
    .join(' OR ');

  destinationFilters = destinationFilters !== '()' ? destinationFilters : '';

  // build the filters based on the selected ships
  const shipFilters: string = selectedShips
    .map((ship) =>
      checkFilterValueInFacetsData('shipCd.key', ship, null) ? `(shipCd.key:"${ship.value}")` : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected itinerary codes
  const itineraryCodes: string = selectedItineraryCodes
    .map((code: string) =>
      checkFilterValueInFacetsData('itinCd', null, code) ? `(itinCd:"${code}")` : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected ports
  const portFilters: string = selectedPorts
    .map((port) =>
      checkFilterValueInFacetsData('embkPort.key', null, port.value)
        ? `(embkPort.key:"${port.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on selected cabins
  let cabinFilters = '';
  if (!isDatesLayer) {
    cabinFilters = selectedCabins
      .map((cabin) =>
        checkFilterValueInFacetsData('macroCategory.key', null, cabin.value)
          ? `(macroCategory.key:"${cabin.value}")`
          : ''
      )
      .filter(Boolean)
      .join(' OR ');
  }

  // build the filters based on selected airports
  let airportFilters = '';
  if (airportToggleEnabled) {
    // Check if it's a deal page with preset airport filters
    const dealPageAirportFilter = dealPage
      ? dealPageFilters.find((filter) => filter.filterType === FilterTypes.AIRPORT)
      : null;

    let airportsToProcess = selectedAirports;

    // If deal page has preset airports and hasAir is selected, replace hasAir with preset values
    const presetAirportCodes = dealPageAirportFilter?.filterValue
      ? normalizeAirportFilterValue(dealPageAirportFilter.filterValue)
      : [];
    if (presetAirportCodes.length > 0 && selectedAirports.some((a) => a.value === 'hasAir')) {
      airportsToProcess = [
        ...selectedAirports.filter((airport) => airport.value !== 'hasAir'),
        ...presetAirportCodes.map((code) => ({ name: code, value: code })),
      ];
    }

    const hasAirAirports = airportsToProcess.filter((airport) => airport.value === 'hasAir');
    const normalAirports = airportsToProcess.filter((airport) => airport.value !== 'hasAir');

    const hasAirFilter = hasAirAirports
      .map((airport) =>
        checkFilterValueInFacetsData('hasAir', null, 'true') ? `${airport.value}:true` : ''
      )
      .join(' AND ');

    const normalFilter = normalAirports
      .map((airport) =>
        checkFilterValueInFacetsData('flights.departureAirportCode', null, airport.value)
          ? `(flights.departureAirportCode:"${airport.value}")`
          : ''
      )
      .filter(Boolean)
      .join(' OR ');

    if (hasAirFilter && normalFilter) {
      airportFilters = `${hasAirFilter} AND ${normalFilter}`;
    } else if (hasAirFilter) {
      airportFilters = hasAirFilter;
    } else {
      airportFilters = normalFilter;
    }
  }

  // build the filters based on the price range
  const promotion = store.getState().dealsConfig?.dealsPromotion;
  let priceRangeFilters = '';
  const sortingState = store.getState().sortingViewPrice;
  const { selectedSortingOptionViewPrice } = sortingState;

  const selectedPricesPriceView =
    selectedPrices &&
    selectedPrices.length > 0 &&
    (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      ? (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      : selectedSortingOptionViewPrice;

  const priceViewPerOption =
    selectedPricesPriceView === OrderByTypeEnum.PERSON ? QueryTypes.perAdult : QueryTypes.perCabin;
  const specificPaxTypePriceFilter = `${QueryTypes.priceAfterDiscount}${promotion?.promotionName}${priceViewPerOption}`;
  const genericPaxTypePriceFilter =
    selectedPricesPriceView === OrderByTypeEnum.PERSON
      ? QueryTypes.adultPrice
      : QueryTypes.cabinPrice;
  const occupancyType = promotion?.id ? specificPaxTypePriceFilter : genericPaxTypePriceFilter; // Price range searchs only for adult price
  const pricesToShow = promotion?.id
    ? QueryTypes.discountedPricesBySpecificPax
    : QueryTypes.pricesAfterDiscount;

  if (selectedPrices && selectedPrices.length == 1) {
    if (selectedPrices[0]?.name === 'minPrice') {
      priceRangeFilters = `(${pricesToShow}.${occupancyType} >= ${selectedPrices[0]?.value})`;
    } else if (selectedPrices[0]?.name === 'maxPrice') {
      priceRangeFilters = `(${pricesToShow}.${occupancyType} <= ${selectedPrices[0]?.value})`;
    }
  }
  if (selectedPrices && selectedPrices.length > 0 && selectedPrices[1]) {
    priceRangeFilters = `(${pricesToShow}.${occupancyType} >= ${selectedPrices[0]?.value} AND ${pricesToShow}.${occupancyType} <= ${selectedPrices[1]?.value})`;
  }

  const availability = `(prices.availability:true)`;

  // build the filters based on the selected dates on monthly and daily calendar
  let dateFilter = '';
  if (selectedDates.dates[0] && selectedDates.dates[1]) {
    const firstDate = selectedDates.dates[0];
    const secondDate = selectedDates.dates[1];
    dateFilter = `(departureStartDateTimestamp >= ${dayjs(firstDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()} AND departureStartDateTimestamp <= ${dayjs(secondDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()})`;
  } else if (selectedPeriods && selectedPeriods.length > 0) {
    const periods = selectedPeriods.map((period) => {
      const parts = period.split(' ');
      const endYear = parts[parts.length - 1];
      const endMonth = parts[parts.length - 2];
      const endDay = parts[parts.length - 3];
      const startYear = parts[parts.length - 4];
      const startMonth = parts[parts.length - 5];
      const startDay = parts[parts.length - 6];

      const startDate = dayjs(`${startYear}-${startMonth}-${startDay}`, 'YYYY-MMM-DD')
        .utc(true)
        .valueOf();
      const endDate = dayjs(`${endYear}-${endMonth}-${endDay}`, 'YYYY-MMM-DD').utc(true).valueOf();

      return `departureStartDateTimestamp:${startDate} TO ${endDate}`;
    });

    // Combine periods with OR and wrap them in parentheses
    dateFilter = `(${periods.join(' OR ')})`;
  }

  // build the numberOfNights filter based on selections
  const numOfNights: string = formatSelectedNumberOfNights(selectedNumberOfNights);
  const promotions =
    selectedPromotions && selectedPromotions.length > 0
      ? `(${selectedPromotions
          .map((promo: FilterOptionSliceType) =>
            dealPage
              ? checkFilterValueInFacetsData('paxType', null, promo.value)
                ? `paxType:"${promo.value}"`
                : ''
              : checkFilterValueInFacetsData('paxTypeTrue', null, promo.value)
              ? `paxTypeTrue:"${promo.value}"`
              : ''
          )
          .filter(Boolean)
          .join(' OR ')})`
      : '';
  const tags: string =
    selectedTags && selectedTags.length > 0
      ? `(${selectedTags
          .map((tag: FilterOptionSliceType) =>
            checkFilterValueInFacetsData('filterTags', null, tag.value)
              ? `filterTags:"${tag.value}"`
              : ''
          )
          .filter(Boolean)
          .join(' OR ')})`
      : '';
  const flyAndCruise: string =
    selectedFlyAndCruise && selectedFlyAndCruise.length > 0
      ? `(${selectedFlyAndCruise
          .map((option: FilterOptionSliceType) =>
            checkFilterValueInFacetsData('hasAir', null, 'true') ? `${option.value}:true` : ''
          )
          .filter(Boolean)
          .join(' AND ')})`
      : '';

  // build the noofAdults, noofMinors and noofJuniors based on guest filter selections
  const guestFilters: string = formatGuestsFilter(selectedGuests);

  // cruiseId filter by the url param
  const cruiseIdFilter = formatCruiseId(cruiseId);

  // priceCode filter by the url param
  const priceCodeFilter = !ignorePriceFiltersForDatesModal ? formatPriceCode(priceCode) : '';

  const priceTypeFilter = !ignorePriceFiltersForDatesModal
    ? formatPriceType(priceTypeFiltered)
    : '';

  // portOfCall filter by the url param
  const portOfCallFilter = formatPortOfCalls(portOfCall);

  if (!toUpdateButton && !isMultifilter) {
    // build the selected filters query string without the active filters
    return [
      itineraryCodes,
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.DESTINATION)
        ? destinationFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.SHIP) ? shipFilters : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PORT) ? portFilters : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.CABIN)
        ? cabinFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.AIRPORT)
        ? airportFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.NUMBER_OF_NIGHTS)
        ? numOfNights
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PROMOTION)
        ? promotions
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.TAG) ? tags : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.FLY_AND_CRUISE)
        ? flyAndCruise
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.GUEST)
        ? guestFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.DATE) ? dateFilter : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_RANGE)
        ? priceRangeFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.CRUISE_ID) &&
      !isDatesLayer
        ? cruiseIdFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_CODE)
        ? priceCodeFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_TYPE)
        ? priceTypeFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PORT_OF_CALL)
        ? portOfCallFilter
        : '',
      isItineraryRequest ? availability : '',
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  } else if (toUpdateButton || (dealPage && !isMultifilter)) {
    // build the selected filters query string with the active filters
    return [
      destinationFilters,
      shipFilters,
      portFilters,
      cabinFilters,
      airportFilters,
      numOfNights,
      promotions,
      tags,
      flyAndCruise,
      guestFilters,
      dateFilter,
      priceRangeFilters,
      cruiseIdFilter,
      priceCodeFilter,
      priceTypeFilter,
      portOfCallFilter,
      availability,
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  } else {
    // is multifilter
    // need to exclude the last filter updated instead of the active filters
    return [
      !lastFilterUpdated.includes(FilterTypes.DESTINATION) || dealPage ? destinationFilters : '',
      !lastFilterUpdated.includes(FilterTypes.SHIP) || dealPage ? shipFilters : '',
      !lastFilterUpdated.includes(FilterTypes.PORT) || dealPage ? portFilters : '',
      !lastFilterUpdated.includes(FilterTypes.CABIN) || dealPage ? cabinFilters : '',
      !lastFilterUpdated.includes(FilterTypes.AIRPORT) || dealPage ? airportFilters : '',
      !lastFilterUpdated.includes(FilterTypes.NUMBER_OF_NIGHTS) || dealPage ? numOfNights : '',
      !lastFilterUpdated.includes(FilterTypes.PROMOTION) || dealPage ? promotions : '',
      !lastFilterUpdated.includes(FilterTypes.TAG) || dealPage ? tags : '',
      !lastFilterUpdated.includes(FilterTypes.FLY_AND_CRUISE) || dealPage ? flyAndCruise : '',
      !lastFilterUpdated.includes(FilterTypes.GUEST) || dealPage ? guestFilters : '',
      !lastFilterUpdated.includes(FilterTypes.DATE) || dealPage ? dateFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_RANGE) || dealPage ? priceRangeFilters : '',
      !lastFilterUpdated.includes(FilterTypes.CRUISE_ID) || dealPage ? cruiseIdFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_CODE) || dealPage ? priceCodeFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_TYPE) || dealPage ? priceTypeFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PORT_OF_CALL) || dealPage ? portOfCallFilter : '',
      availability,
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  }
}

export function buildSelectedFilterExcursionsQueryString(
  toUpdateButton = false,
  isMultifilter = false
): string {
  const state = store.getState();
  const dealPage = isDealsPage();
  const selectedDestinationsExcursions = state.facetFilter.selectedDestinationsExcursions;

  const selectedPortCode = state.facetFilter.selectedPortsExcursions;

  const selectedCategories = state.facetFilter.selectedCategories;
  const lastFilterUpdated = state.lastFilterUpdated.filterType;
  const activeFilterIds = state.activeFilter.activeFilterIds;

  // build the filters based on the selected destinations
  const destinationsFilters: string = selectedDestinationsExcursions
    .map((destination) =>
      checkFilterValueInFacetsData('destinationEscursions', destination, null)
        ? `(destination:"${destination.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');
  // build the filters based on the selected ships
  const categoryFilters: string = selectedCategories
    .map((category) =>
      checkFilterValueInFacetsData('categoryCd', category, null)
        ? `(categoryCd:"${category.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected itinerary codes

  // build the filters based on the selected ports
  const portCodeFilters: string = selectedPortCode
    .map((port) =>
      checkFilterValueInFacetsData('portExcursions', null, port.value)
        ? `(portCode:"${port.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected dates on monthly and daily calendar

  // build the numberOfNights filter based on selections

  if (!toUpdateButton && !isMultifilter) {
    // build the selected filters query string without the active filters
    const categoryFilter = !activeFilterIds.some(
      ({ filterType }) => filterType === FilterTypes.CATEGORY
    )
      ? categoryFilters
      : '';

    const portCodeFilter = !activeFilterIds.some(
      ({ filterType }) => filterType === FilterTypes.PORT_EXCURSIONS
    )
      ? portCodeFilters
      : '';

    const destinationFilter = !activeFilterIds.some(
      ({ filterType }) => filterType === FilterTypes.DESTINATION_EXCURSIONS
    )
      ? destinationsFilters
      : '';

    const mainFilters = [categoryFilter, portCodeFilter].filter(Boolean).join(' AND ');

    let selectedFilters = destinationFilter
      ? mainFilters
        ? `${mainFilters} | ${destinationFilter}`
        : destinationFilter
      : mainFilters;

    // Append "isPackage:true" to the selectedFilters since the packages must not be sold
    selectedFilters = selectedFilters
      ? `${selectedFilters} AND isPackage:false`
      : 'isPackage:false';
    return selectedFilters;
  } else if (toUpdateButton || (dealPage && !isMultifilter)) {
    // build the selected filters query string with the active filters
    const filters = [categoryFilters, portCodeFilters].filter(Boolean).join(' AND ');

    let selectedFilters = destinationsFilters
      ? filters
        ? `${filters} | ${destinationsFilters}`
        : destinationsFilters
      : filters;

    // Append "isPackage:true" to the selectedFilters since the packages must not be sold
    selectedFilters = selectedFilters
      ? `${selectedFilters} AND isPackage:false`
      : 'isPackage:false';

    return selectedFilters;
  } else {
    // is multifilter
    // need to exclude the last filter updated instead of the active filters
    const categoryFilter =
      !lastFilterUpdated.includes(FilterTypes.CATEGORY) || dealPage ? categoryFilters : '';
    const portCodeFilter =
      !lastFilterUpdated.includes(FilterTypes.PORT_EXCURSIONS) || dealPage ? portCodeFilters : '';
    const destinationFilter =
      !lastFilterUpdated.includes(FilterTypes.DESTINATION_EXCURSIONS) || dealPage
        ? destinationsFilters
        : '';

    const mainFilters = [categoryFilter, portCodeFilter].filter(Boolean).join(' AND ');

    let selectedFilters = destinationFilter
      ? mainFilters
        ? `${mainFilters} | ${destinationFilter}`
        : destinationFilter
      : mainFilters;

    // Append "isPackage:true" to the selectedFilters since the packages must not be sold
    selectedFilters = selectedFilters
      ? `${selectedFilters} AND isPackage:false`
      : 'isPackage:false';

    return selectedFilters;
  }
}

export function buildSelectedFilterQueryStringCruises(
  isDatesLayer = false,
  toUpdateButton = false,
  isMultifilter = false,
  isItineraryRequest = false,
  isWishlist = false,
  ignorePriceFiltersForDatesModal?: boolean,
  ignoreCruiseId = false
): string {
  const state = store.getState();
  const dealPage = isDealsPage();
  const selectedDestinations = state.facetFilter.selectedDestinations;
  const selectedShips = state.facetFilter.selectedShips;
  const selectedPorts = state.facetFilter.selectedPorts;
  const selectedCabins = state.facetFilter.selectedCabins;
  const selectedNumberOfNights = state.genericFilter.numberOfNights;
  const selectedPromotions = state.genericFilter.promotion;
  const selectedTags = state.genericFilter.tag;
  const selectedGuests = state.guestFilter;
  const selectedDates = state.dateFilter.selectedDates;
  const selectedPeriods = state.dateFilter.selectedPeriods;
  const selectedPrices = state.facetFilter.finalPriceRange;
  const cruiseId = state.facetFilter.selectedCruiseId;
  const portOfCall = state.facetFilter.selectedPortOfCall;
  const priceTypeFiltered = state.facetFilter.selectedPriceType;
  const priceCode = state.facetFilter.selectedPriceCode;
  const lastFilterUpdated = state.lastFilterUpdated.filterType;
  const activeFilterIds = state.activeFilter.activeFilterIds;
  let selectedItineraryCodes: string[] = [];
  if (isWishlist) selectedItineraryCodes = mscGet<string[]>('local', 'wishlist') ?? [];

  // build the filters based on the selected destinations
  let destinationFilters: string = selectedDestinations
    .map((dest) => {
      const clauses = dest.value
        .split('-')
        .filter((d) => d && checkFilterValueInFacetsData('commArea.key', null, d))
        .map((d) => `commArea.key:"${d}"`)
        .filter(Boolean)
        .join(' OR ');

      return clauses ? `(${clauses})` : '';
    })
    .filter(Boolean)
    .join(' OR ');

  destinationFilters = destinationFilters !== '()' ? destinationFilters : '';

  // build the filters based on the selected ships
  const shipFilters: string = selectedShips
    .map((ship) =>
      checkFilterValueInFacetsData('shipCd.key', ship, null) ? `(shipCd.key:"${ship.value}")` : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected itinerary codes
  const itineraryCodes: string = selectedItineraryCodes
    .map((code: string) =>
      checkFilterValueInFacetsData('itinCd', null, code) ? `(itinCd:"${code}")` : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected ports
  const portFilters: string = selectedPorts
    .map((port) =>
      checkFilterValueInFacetsData('embkPort.key', null, port.value)
        ? `(embkPort.key:"${port.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on selected cabins
  let cabinFilters = '';
  if (!isDatesLayer) {
    cabinFilters = selectedCabins
      .map((cabin) =>
        checkFilterValueInFacetsData('macroCategory.key', null, cabin.value)
          ? `(macroCategory.key:"${cabin.value}")`
          : ''
      )
      .filter(Boolean)
      .join(' OR ');
  }

  // build the filters based on the price range
  const promotion = store.getState().dealsConfig?.dealsPromotion;
  let priceRangeFilters = '';
  const sortingState = store.getState().sortingViewPrice;
  const { selectedSortingOptionViewPrice } = sortingState;
  const selectedPricesPriceView =
    selectedPrices &&
    selectedPrices.length > 0 &&
    (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      ? (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      : selectedSortingOptionViewPrice;

  const priceViewPerOption =
    selectedPricesPriceView === OrderByTypeEnum.PERSON ? QueryTypes.perAdult : QueryTypes.perCabin;
  const specificPaxTypePriceFilter = `${QueryTypes.priceAfterDiscount}${promotion?.promotionName}${priceViewPerOption}`;
  const genericPaxTypePriceFilter =
    selectedPricesPriceView === OrderByTypeEnum.PERSON
      ? QueryTypes.adultPrice
      : QueryTypes.cabinPrice;
  const occupancyType = promotion?.id ? specificPaxTypePriceFilter : genericPaxTypePriceFilter; // Price range searchs only for adult price
  const pricesToShow = promotion?.id
    ? QueryTypes.discountedPricesBySpecificPax
    : QueryTypes.pricesAfterDiscount;

  if (selectedPrices && selectedPrices.length == 1) {
    if (selectedPrices[0]?.name === 'minPrice') {
      priceRangeFilters = `(${pricesToShow}.${occupancyType} >= ${selectedPrices[0]?.value})`;
    } else if (selectedPrices[0]?.name === 'maxPrice') {
      priceRangeFilters = `(${pricesToShow}.${occupancyType} <= ${selectedPrices[0]?.value})`;
    }
  }
  if (selectedPrices && selectedPrices.length > 0 && selectedPrices[1]) {
    priceRangeFilters = `(${pricesToShow}.${occupancyType} >= ${selectedPrices[0]?.value} AND ${pricesToShow}.${occupancyType} <= ${selectedPrices[1]?.value})`;
  }

  const availability = `(prices.availability:true)`;

  // build the filters based on the selected dates on monthly and daily calendar
  let dateFilter = '';
  if (selectedDates.dates[0] && selectedDates.dates[1]) {
    const firstDate = selectedDates.dates[0];
    const secondDate = selectedDates.dates[1];
    dateFilter = `(departureStartDateTimestamp >= ${dayjs(firstDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()} AND departureStartDateTimestamp <= ${dayjs(secondDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()})`;
  } else if (selectedPeriods && selectedPeriods.length > 0) {
    const periods = selectedPeriods.map((period) => {
      const parts = period.split(' ');
      const endYear = parts[parts.length - 1];
      const endMonth = parts[parts.length - 2];
      const endDay = parts[parts.length - 3];
      const startYear = parts[parts.length - 4];
      const startMonth = parts[parts.length - 5];
      const startDay = parts[parts.length - 6];

      const startDate = dayjs(`${startYear}-${startMonth}-${startDay}`, 'YYYY-MMM-DD')
        .utc(true)
        .valueOf();
      const endDate = dayjs(`${endYear}-${endMonth}-${endDay}`, 'YYYY-MMM-DD').utc(true).valueOf();

      return `departureStartDateTimestamp:${startDate} TO ${endDate}`;
    });

    // Combine periods with OR and wrap them in parentheses
    dateFilter = `(${periods.join(' OR ')})`;
  }

  // build the numberOfNights filter based on selections
  const numOfNights: string = formatSelectedNumberOfNights(selectedNumberOfNights);
  const facetsExist = store.getState().search?.facetsData?.facets;
  const isBookingFunnel = !facetsExist;

  const promotions =
    selectedPromotions && selectedPromotions.length > 0
      ? `(${selectedPromotions
          .map((promo: FilterOptionSliceType) => {
            if (isBookingFunnel) {
              return `paxType:"${promo.value}"`;
            }
            return dealPage || state.bookingParams.isDeal
              ? checkFilterValueInFacetsData('paxType', null, promo.value)
                ? `paxType:"${promo.value}"`
                : ''
              : checkFilterValueInFacetsData('paxTypeTrue', null, promo.value)
              ? `paxTypeTrue:"${promo.value}"`
              : '';
          })
          .filter(Boolean)
          .join(' OR ')})`
      : '';
  const tags: string =
    selectedTags && selectedTags.length > 0
      ? `(${selectedTags
          .map((tag: FilterOptionSliceType) =>
            checkFilterValueInFacetsData('filterTags', null, tag.value)
              ? `filterTags:"${tag.value}"`
              : ''
          )
          .filter(Boolean)
          .join(' OR ')})`
      : '';

  // build the noofAdults, noofMinors and noofJuniors based on guest filter selections
  const guestFilters: string = formatGuestsFilter(selectedGuests);

  // cruiseId filter by the url param
  const cruiseIdFilter = formatCruiseId(cruiseId);

  // priceCode filter by the url param
  const priceCodeFilter = !ignorePriceFiltersForDatesModal ? formatPriceCode(priceCode) : '';

  const priceTypeFilter = !ignorePriceFiltersForDatesModal
    ? formatPriceType(priceTypeFiltered)
    : '';

  // portOfCall filter by the url param
  const portOfCallFilter = formatPortOfCalls(portOfCall);

  if (!toUpdateButton && !isMultifilter) {
    // build the selected filters query string without the active filters
    return [
      itineraryCodes,
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.DESTINATION)
        ? destinationFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.SHIP) ? shipFilters : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PORT) ? portFilters : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.CABIN)
        ? cabinFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.NUMBER_OF_NIGHTS)
        ? numOfNights
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PROMOTION)
        ? promotions
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.TAG) ? tags : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.GUEST)
        ? guestFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.DATE) ? dateFilter : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_RANGE)
        ? priceRangeFilters
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.CRUISE_ID) &&
      !ignoreCruiseId
        ? cruiseIdFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_CODE)
        ? priceCodeFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PRICE_TYPE)
        ? priceTypeFilter
        : '',
      !activeFilterIds.some(({ filterType }) => filterType === FilterTypes.PORT_OF_CALL)
        ? portOfCallFilter
        : '',
      isItineraryRequest ? availability : '',
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  } else if (toUpdateButton || (dealPage && !isMultifilter)) {
    // build the selected filters query string with the active filters
    return [
      destinationFilters,
      shipFilters,
      portFilters,
      cabinFilters,
      numOfNights,
      promotions,
      tags,
      guestFilters,
      dateFilter,
      priceRangeFilters,
      cruiseIdFilter,
      priceCodeFilter,
      priceTypeFilter,
      portOfCallFilter,
      availability,
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  } else {
    // is multifilter
    // need to exclude the last filter updated instead of the active filters
    return [
      !lastFilterUpdated.includes(FilterTypes.DESTINATION) || dealPage ? destinationFilters : '',
      !lastFilterUpdated.includes(FilterTypes.SHIP) || dealPage ? shipFilters : '',
      !lastFilterUpdated.includes(FilterTypes.PORT) || dealPage ? portFilters : '',
      !lastFilterUpdated.includes(FilterTypes.CABIN) || dealPage ? cabinFilters : '',
      !lastFilterUpdated.includes(FilterTypes.NUMBER_OF_NIGHTS) || dealPage ? numOfNights : '',
      !lastFilterUpdated.includes(FilterTypes.PROMOTION) || dealPage ? promotions : '',
      !lastFilterUpdated.includes(FilterTypes.TAG) || dealPage ? tags : '',
      !lastFilterUpdated.includes(FilterTypes.GUEST) || dealPage ? guestFilters : '',
      !lastFilterUpdated.includes(FilterTypes.DATE) || dealPage ? dateFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_RANGE) || dealPage ? priceRangeFilters : '',
      !lastFilterUpdated.includes(FilterTypes.CRUISE_ID) || dealPage ? cruiseIdFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_CODE) || dealPage ? priceCodeFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PRICE_TYPE) || dealPage ? priceTypeFilter : '',
      !lastFilterUpdated.includes(FilterTypes.PORT_OF_CALL) || dealPage ? portOfCallFilter : '',
      availability,
    ]
      .filter((filter) => filter !== '')
      .join(' AND ');
  }
}

export function buildMultiFilterQueryString(filterType: string, isDatesLayer?: boolean) {
  const state = store.getState();
  const dealPage = isDealsPage();
  const selectedDestinations = state.facetFilter.selectedDestinations;
  const selectedShips = state.facetFilter.selectedShips;
  const selectedPorts = state.facetFilter.selectedPorts;
  const selectedCabins = state.facetFilter.selectedCabins;
  const selectedAirports = state.facetFilter.selectedAirports;
  const selectedNumberOfNights = state.genericFilter.numberOfNights;
  const selectedPromotions = state.genericFilter.promotion;
  const selectedTags = state.genericFilter.tag;
  const selectedFlyAndCruise = state.genericFilter.flyAndCruise;
  const selectedGuests = state.guestFilter;
  const selectedDates = state.dateFilter.selectedDates;
  const selectedPeriods = state.dateFilter.selectedPeriods;
  const selectedPrices = state.facetFilter.finalPriceRange;
  const cruiseId = state.facetFilter.selectedCruiseId;
  const portOfCall = state.facetFilter.selectedPortOfCall;
  const priceCode = state.facetFilter.selectedPriceCode;
  const priceTypeFiltered = state.facetFilter.selectedPriceType;
  const moreFilters = state.multiFilter.moreFilters;

  // build the filters based on the selected destinations
  let destinationFilters: string = selectedDestinations
    .map(
      (dest) =>
        `(${dest.value
          .split('-')
          .filter(
            (d) =>
              typeof d === 'string' &&
              d !== '' &&
              d !== undefined &&
              d !== null &&
              checkFilterValueInFacetsData('commArea.key', null, d)
          )
          .map((d: string) => `commArea.key:"${d}"`)
          .filter(Boolean)
          .join(' OR ')})`
    )
    .filter(Boolean)
    .join(' OR ');

  destinationFilters = destinationFilters !== '()' ? destinationFilters : '';

  // build the filters based on the selected ships
  const shipFilters: string = selectedShips
    .map((ship) =>
      checkFilterValueInFacetsData('shipCd.key', ship, null) ? `(shipCd.key:"${ship.value}")` : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on the selected ports
  const portFilters: string = selectedPorts
    .map((port) =>
      checkFilterValueInFacetsData('embkPort.key', null, port.value)
        ? `(embkPort.key:"${port.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  // build the filters based on selected cabins
  let cabinFilters = '';
  if (!isDatesLayer) {
    cabinFilters = selectedCabins
      .map((cabin) =>
        checkFilterValueInFacetsData('macroCategory.key', null, cabin.value)
          ? `(macroCategory.key:"${cabin.value}")`
          : ''
      )
      .filter(Boolean)
      .join(' OR ');
  }

  // build the filters based on selected airports
  let airportFilters = '';
  const hasAirAirports = selectedAirports.filter((airport) => airport.value === 'hasAir');
  const normalAirports = selectedAirports.filter((airport) => airport.value !== 'hasAir');

  const hasAirFilter = hasAirAirports
    .map((airport) =>
      checkFilterValueInFacetsData('hasAir', null, 'true') ? `${airport.value}:true` : ''
    )
    .join(' AND ');

  const normalFilter = normalAirports
    .map((airport) =>
      checkFilterValueInFacetsData('flights.departureAirportCode', null, airport.value)
        ? `(flights.departureAirportCode:"${airport.value}")`
        : ''
    )
    .filter(Boolean)
    .join(' OR ');

  if (hasAirFilter && normalFilter) {
    airportFilters = `${hasAirFilter} AND ${normalFilter}`;
  } else if (hasAirFilter) {
    airportFilters = hasAirFilter;
  } else {
    airportFilters = normalFilter;
  }

  // build the filters based on the price range
  let priceRangeFilters = '';
  let priceType = '';

  // build the filters based on the price range
  const promotion = store.getState().dealsConfig?.dealsPromotion;
  const pricesToShow = promotion?.id
    ? QueryTypes.discountedPricesBySpecificPax
    : QueryTypes.pricesAfterDiscount;

  const sortingState = store.getState().sortingViewPrice;
  const { selectedSortingOptionViewPrice } = sortingState;

  const selectedPricesPriceView =
    selectedPrices &&
    selectedPrices.length > 0 &&
    (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      ? (selectedPrices[0] as PriceRangeFilterOptionType).viewType
      : selectedSortingOptionViewPrice;

  if (selectedPricesPriceView === OrderByTypeEnum.PERSON) {
    priceType = promotion?.id
      ? `${QueryTypes.priceAfterDiscount}${promotion?.promotionName}${QueryTypes.perAdult}`
      : QueryTypes.adultPrice; // Price range searchs only for adult price // Price range searchs only for adult price
  } else if (selectedPricesPriceView === OrderByTypeEnum.CABIN) {
    priceType = promotion?.id
      ? `${QueryTypes.priceAfterDiscount}${promotion?.promotionName}${QueryTypes.perCabin}`
      : QueryTypes.cabinPrice; // Price range searchs only for adult price // Price range searchs only for cabin price
  }

  if (selectedPrices && selectedPrices.length == 1) {
    if (selectedPrices[0]?.name === 'minPrice') {
      priceRangeFilters = `(${pricesToShow}.${priceType} >= ${selectedPrices[0]?.value})`;
    } else if (selectedPrices[0]?.name === 'maxPrice') {
      priceRangeFilters = `(${pricesToShow}.${priceType} <= ${selectedPrices[0]?.value})`;
    }
  }
  if (selectedPrices && selectedPrices.length > 0 && selectedPrices[1]) {
    priceRangeFilters = `(${pricesToShow}.${priceType} >= ${selectedPrices[0]?.value} AND ${pricesToShow}.${priceType} <= ${selectedPrices[1]?.value})`;
  }

  // build the filters based on the selected dates on monthly and daily calendar
  let dateFilter = '';
  if (selectedDates.dates[0] && selectedDates.dates[1]) {
    const firstDate = selectedDates.dates[0];
    const secondDate = selectedDates.dates[1];
    dateFilter = `(departureStartDateTimestamp >= ${dayjs(firstDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()} AND departureStartDateTimestamp <= ${dayjs(secondDate.replace(/-/g, '/'))
      .utc(true)
      .valueOf()})`;
  } else if (selectedPeriods && selectedPeriods.length > 0) {
    const periods = selectedPeriods.map((period) => {
      const parts = period.split(' ');
      const endYear = parts[parts.length - 1];
      const endMonth = parts[parts.length - 2];
      const endDay = parts[parts.length - 3];
      const startYear = parts[parts.length - 4];
      const startMonth = parts[parts.length - 5];
      const startDay = parts[parts.length - 6];

      const startDate = dayjs(`${startYear}-${startMonth}-${startDay}`, 'YYYY-MMM-DD')
        .utc(true)
        .valueOf();
      const endDate = dayjs(`${endYear}-${endMonth}-${endDay}`, 'YYYY-MMM-DD').utc(true).valueOf();

      return `departureStartDateTimestamp:${startDate} TO ${endDate}`;
    });

    // Combine periods with OR and wrap them in parentheses
    dateFilter = `(${periods.join(' OR ')})`;
  }

  // build the numberOfNights filter based on selections
  const numOfNights: string = formatSelectedNumberOfNights(selectedNumberOfNights);
  const tags: string =
    selectedTags && selectedTags.length > 0
      ? `(${selectedTags
          .map((tag: FilterOptionSliceType) =>
            checkFilterValueInFacetsData('filterTags', null, tag.value)
              ? `filterTags:"${tag.value}"`
              : ''
          )
          .filter(Boolean)
          .join(' OR ')})`
      : '';
  const promotions =
    selectedPromotions && selectedPromotions.length > 0
      ? `(${selectedPromotions
          .map((promo: FilterOptionSliceType) =>
            dealPage ? `paxType:"${promo.value}"` : `paxTypeTrue:"${promo.value}"`
          )
          .join(' OR ')})`
      : '';
  const flyAndCruise: string =
    selectedFlyAndCruise && selectedFlyAndCruise.length > 0
      ? `(${selectedFlyAndCruise
          .map((option: FilterOptionSliceType) => `${option.value}:true`)
          .join(' AND ')})`
      : '';

  // build the noofAdults, noofMinors and noofInfants based on guest filter selections
  const guestFilters: string = formatGuestsFilter(selectedGuests);

  // cruiseId filter by the url param
  const cruiseIdFilter = formatCruiseId(cruiseId);

  // priceCode filter by the url param
  const priceCodeFilter = formatPriceCode(priceCode);

  // priceType filter by the url param
  const priceTypeFilter = formatPriceType(priceTypeFiltered);

  // portOfCall filter by the url param
  const portOfCallFilter = formatPortOfCalls(portOfCall);

  return [
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.DESTINATION && filter.filterType === filterType
    )
      ? ''
      : destinationFilters,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.SHIP && filter.filterType === filterType
    )
      ? ''
      : shipFilters,
    moreFilters.some(
      (filter) => filter.filterId === FilterTypes.PORT && filter.filterType === filterType
    )
      ? ''
      : portFilters,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.CABIN && filter.filterType === filterType
    )
      ? ''
      : cabinFilters,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.AIRPORT && filter.filterType === filterType
    )
      ? ''
      : airportFilters,
    moreFilters.some(
      (filter) =>
        filter.filterType === FilterTypes.NUMBER_OF_NIGHTS && filter.filterType === filterType
    )
      ? ''
      : numOfNights,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.PROMOTION && filter.filterType === filterType
    )
      ? ''
      : promotions,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.TAG && filter.filterType === filterType
    )
      ? ''
      : tags,
    moreFilters.some(
      (filter) =>
        filter.filterType === FilterTypes.FLY_AND_CRUISE && filter.filterType === filterType
    )
      ? ''
      : flyAndCruise,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.GUEST && filter.filterType === filterType
    )
      ? ''
      : guestFilters,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.DATE && filter.filterType === filterType
    )
      ? ''
      : dateFilter,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.PRICE_RANGE && filter.filterType === filterType
    )
      ? ''
      : priceRangeFilters,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.CRUISE_ID && filter.filterType === filterType
    )
      ? ''
      : cruiseIdFilter,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.PRICE_CODE && filter.filterType === filterType
    )
      ? ''
      : priceCodeFilter,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.PRICE_TYPE && filter.filterType === filterType
    )
      ? ''
      : priceTypeFilter,
    moreFilters.some(
      (filter) => filter.filterType === FilterTypes.PORT_OF_CALL && filter.filterType === filterType
    )
      ? ''
      : portOfCallFilter,
  ]
    .filter((filter) => filter !== '')
    .join(' AND ');
}

export function buildFacetsOnlyQuery() {
  return `(prices.availability:true)`;
}

export function checkFilterValueInFacetsData(
  filterType: string,
  filterValue: FilterOptionSliceType | null,
  filterValueString: string | null
) {
  const facetsData: any = store.getState().search?.facetsData?.facets;
  if (facetsData == null || facetsData?.facets?.length == 0) return true;

  // Check if filterType is an array
  if (facetsData[filterType]) {
    if (Array.isArray(facetsData[filterType])) {
      // Check
      if (
        facetsData[filterType].some((facet) =>
          filterValue ? facet.key === filterValue.value : facet.key === filterValueString
        )
      ) {
        return true;
      }
    } else if (Object.keys(facetsData[filterType])?.length > 0) {
      // Check if filterValue is found in the array
      const keys = Object.keys(facetsData[filterType]);
      return filterValue
        ? keys.includes(filterValue.value)
        : filterValueString
        ? keys.includes(filterValueString)
        : false;
    }
  }

  // Return false if filterType is not an array or if the filterValue is not found in the array
  return false;
}
